import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { generalAPI, jobsAPI, servicesAPI } from '../utils/api';
import JobCard from '../components/jobs/JobCard';
import ServiceCard from '../components/services/ServiceCard';
import './Home.css';

const SPORT_CATEGORIES = [
  { icon: '⚽', name: 'كرة القدم', en: 'Football' },
  { icon: '🏀', name: 'كرة السلة', en: 'Basketball' },
  { icon: '🏊', name: 'السباحة', en: 'Swimming' },
  { icon: '🏃', name: 'ألعاب القوى', en: 'Running' },
  { icon: '🎾', name: 'التنس', en: 'Tennis' },
  { icon: '🥋', name: 'فنون قتالية', en: 'Judo' },
  { icon: '🏐', name: 'كرة الطائرة', en: 'Badminton' },
  { icon: '🏅', name: 'رياضات متعددة', en: '' },
];


const WORLD_FEDERATIONS = [
  { name: 'Olympics', nameAr: 'اللجنة الأولمبية الدولية', url: 'https://www.olympics.com/ar/', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/5c/Olympic_rings_without_rims.svg/320px-Olympic_rings_without_rims.svg.png', color: '#000000', bg: '#F5F5F5' },
  { name: 'ISSF', nameAr: 'الاتحاد الدولي للرماية', url: 'https://www.issf-sports.org/', logo: 'https://www.issf-sports.org/get_image.ashx?id=5038', color: '#1A5EA8', bg: '#E8F1FB' },
  { name: 'Asia Shooting', nameAr: 'اتحاد الرماية الآسيوي', url: 'https://www.asia-shooting.org/', logo: 'https://www.asia-shooting.org/wp-content/uploads/2020/01/ASC-Logo.png', color: '#D12F2F', bg: '#FDECEA' },
  { name: 'Arab Shooting', nameAr: 'الاتحاد العربي للرماية', url: 'http://www.arab-shooting.org/', logo: 'http://www.arab-shooting.org/images/logo.png', color: '#2E8B4A', bg: '#EAF6EF' },
  { name: 'FITASC', nameAr: 'الاتحاد الدولي للصيد الرياضي', url: 'https://www.fitasc.com/en', logo: 'https://www.fitasc.com/images/logo-fitasc.png', color: '#4A3B8C', bg: '#F3EBF8' },
  { name: 'IPSC', nameAr: 'الاتحاد الدولي للرماية العملية', url: 'https://www.ipsc.org/', logo: 'https://www.ipsc.org/wp-content/uploads/2018/11/IPSC_Logo_header.png', color: '#C49A00', bg: '#FEF5E7' },
];

const HomePage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({ jobs: 0, users: 0, services: 0, organizations: 0 });
  const [featuredJobs, setFeaturedJobs] = useState([]);
  const [featuredServices, setFeaturedServices] = useState([]);

  useEffect(() => {
    generalAPI.getStats().then(r => setStats(r.data.stats || { jobs: 0, users: 0, services: 0, organizations: 0 })).catch(() => {});
    jobsAPI.getJobs({ limit: 6, sort: 'newest' }).then(r => setFeaturedJobs(r.data.jobs || [])).catch(() => {});
    servicesAPI.getServices({ limit: 6, sort: 'popular' }).then(r => setFeaturedServices(r.data.services || [])).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) navigate(`/jobs?search=${encodeURIComponent(searchQuery)}`);
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-orb hero-orb-1" />
          <div className="hero-orb hero-orb-2" />
          <div className="hero-grid" />
        </div>
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">🏆 منصة رياضية رقم 1</div>
            <h1 className="hero-title">
              اكتشف فرصتك<br />
              <span className="hero-highlight">الوظائف الرياضية</span>
            </h1>
            <p className="hero-subtitle">
              منصة التوظيف والخدمات الرياضية المتخصصة. تواصل مع أفضل الأندية والأكاديميات والمؤسسات الرياضية.
            </p>
            <form onSubmit={handleSearch} className="hero-search">
              <div className="hero-search-inner">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="ابحث عن وظيفة، خدمة، أو محترف رياضي..."
                  className="hero-search-input"
                />
                <button type="submit" className="btn btn-primary">بحث</button>
              </div>
            </form>
            <div className="hero-quick-links">
              <span className="text-muted text-sm">الأكثر بحثاً:</span>
              {['مدرب كرة قدم', 'حكم دولي', 'أخصائي طب رياضي', 'تصوير رياضي'].map(q => (
                <button key={q} onClick={() => navigate(`/jobs?search=${encodeURIComponent(q)}`)} className="quick-link">{q}</button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {[
              { icon: '💼', value: stats.jobs, label: 'وظيفة متاحة', link: '/jobs' },
              { icon: '👥', value: stats.users, label: 'محترف رياضي', link: '/professionals' },
              { icon: '⚡', value: stats.services, label: 'خدمة معروضة', link: '/services' },
              { icon: '🏆', value: stats.organizations, label: 'مؤسسة رياضية', link: '/organizations' },
            ].map((s, i) => (
              <Link key={i} to={s.link} className="stat-card" style={{textDecoration:'none',cursor:'pointer'}}>
                <div className="stat-icon">{s.icon}</div>
                <div className="stat-value">{s.value > 1000 ? `${(s.value/1000).toFixed(1)}K` : s.value}+</div>
                <div className="stat-label">{s.label}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Sport Categories */}
      <section className="categories-section">
        <div className="container">
          <div className="section-header text-center">
            <h2><span className="section-title-line">الرياضات المتاحة</span></h2>
            <p>اكتشف فرص عمل وخدمات في مختلف الرياضات</p>
          </div>
          <div className="categories-grid">
            {SPORT_CATEGORIES.map(cat => (
              <Link key={cat.name} to={`/jobs?sport_category=${encodeURIComponent(cat.en || cat.name)}`} className="category-card">
                <span className="cat-icon">{cat.icon}</span>
                <span className="cat-name">{cat.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Jobs */}
      <section className="section-jobs">
        <div className="container">
          <div className="section-header flex-between">
            <div>
              <h2><span className="section-title-line">أحدث الوظائف</span></h2>
              <p className="text-muted mt-8">فرص عمل في القطاع الرياضي</p>
            </div>
            <Link to="/jobs" className="btn btn-secondary">عرض الكل</Link>
          </div>
          {featuredJobs.length > 0 ? (
            <div className="grid-3">{featuredJobs.map(j => <JobCard key={j.uuid} job={j} />)}</div>
          ) : (
            <div className="empty-state"><div className="icon">💼</div><h3>لا توجد وظائف حالياً</h3></div>
          )}
        </div>
      </section>

      {/* Featured Services */}
      <section className="section-services">
        <div className="container">
          <div className="section-header flex-between">
            <div>
              <h2><span className="section-title-line">الخدمات الرياضية</span></h2>
              <p className="text-muted mt-8">استعن بمحترفين رياضيين لمشاريعك</p>
            </div>
            <Link to="/services" className="btn btn-secondary">عرض الكل</Link>
          </div>
          {featuredServices.length > 0 ? (
            <div className="grid-3">{featuredServices.map(s => <ServiceCard key={s.uuid} service={s} />)}</div>
          ) : (
            <div className="empty-state"><div className="icon">⚡</div><h3>لا توجد خدمات حالياً</h3></div>
          )}
        </div>
      </section>

      {/* How it works */}
      <section className="how-section">
        <div className="container">
          <div className="section-header text-center">
            <h2><span className="section-title-line">كيف تعمل المنصة؟</span></h2>
          </div>
          <div className="how-grid">
            {[
              { step: '01', icon: '📝', title: 'أنشئ ملفك الشخصي', desc: 'سجّل وأضف خبراتك ومهاراتك ومؤهلاتك الرياضية بشكل احترافي.' },
              { step: '02', icon: '🔍', title: 'ابحث واستكشف', desc: 'تصفح آلاف الوظائف والخدمات والمحترفين في القطاع الرياضي.' },
              { step: '03', icon: '🤝', title: 'تواصل وتعاقد', desc: 'تواصل مباشرة مع الجهات والمؤسسات الرياضية وأتمم الصفقة.' },
            ].map(s => (
              <div key={s.step} className="how-card card">
                <div className="how-step">{s.step}</div>
                <div className="how-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-card">
            <div className="cta-content">
              <h2>جاهز للانطلاق؟</h2>
              <p>انضم إلى آلاف المحترفين الرياضيين والمؤسسات على SPORTIX</p>
            </div>
            <div className="cta-actions">
              <Link to="/register" className="btn btn-primary btn-lg">ابدأ مجاناً</Link>
              <Link to="/jobs" className="btn btn-secondary btn-lg">استعرض الوظائف</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
